import { Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { MdMovieFilter } from "react-icons/md";

import { IClip } from "MVVM/@types";
import { Divider } from "MVVM/Components/Divider";
import { analytics } from "MVVM/Analytics";
import { useUser } from "MVVM/Hooks/useUser";
import { InfoSection } from "../InfoSection";
import { ShareSection } from "../ShareSection";

interface FullMatchVodSidebarProps {
  clip: IClip;
  matchId: string;
  onRemixClicked: () => void;
}

/**
 * Entire sidebar view used in place of the default (Recommended / tabs) layout
 * when the clip is a full match VOD. Keeps the clip identity and share controls
 * but swaps the main column for a CTA into the full match VOD experience.
 */
export const FullMatchVodSidebar = ({
  clip,
  matchId,
  onRemixClicked,
}: FullMatchVodSidebarProps) => {
  const history = useHistory();
  const { allstarUser } = useUser();

  const handleOpen = () => {
    analytics.track("Match VOD - View Full Experience From Clip", { matchId });
    const params = new URLSearchParams({ vod: matchId });
    // The viewer fetches its own data from matchId + playerGameId, so it can
    // open straight from the URL without waiting for the match row to load.
    // For now use the viewer's own Steam id (match VODs are CS2); fall back to
    // the clip's identifier if present.
    const playerGameId =
      allstarUser?.user?.steam?.steamid || clip.playerGameIdentifier;
    if (playerGameId) {
      params.set("playerGameId", playerGameId);
    }
    history.push(`/match-history?${params.toString()}`);
  };

  return (
    <Container>
      <InfoSection clip={clip} />
      <Divider />
      <VodPanel>
        <Eyebrow>
          <MdMovieFilter size={18} />
          Full Match VOD
        </Eyebrow>
        <VodTitle>Watch the full match experience</VodTitle>
        <VodDesc>
          This clip is part of a full match VOD. Rewatch every round, jump
          straight to your best plays, and turn any moment into a clip in a
          single click.
        </VodDesc>
        <OpenButton onClick={handleOpen}>
          <MdMovieFilter size={16} />
          Open full match VOD
        </OpenButton>
      </VodPanel>
      <Divider />
      <ShareSection clip={clip} onRemixClicked={onRemixClicked} />
    </Container>
  );
};

const Container = styled(Flex)`
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`;

const VodPanel = styled(Flex)`
  align-items: flex-start;
  background: ${({ theme }) => `${theme.colors.envy}0d`};
  border: 1px solid ${({ theme }) => `${theme.colors.envy}30`};
  border-radius: 12px;
  flex-direction: column;
  gap: 10px;
  margin: 16px;
  padding: 18px;
`;

const Eyebrow = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  font-family: Barlow;
  font-size: 13px;
  font-weight: 800;
  gap: 7px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const VodTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.15;
`;

const VodDesc = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
`;

const OpenButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 700;
  gap: 7px;
  justify-content: center;
  margin-top: 4px;
  padding: 11px 16px;
  transition: opacity 0.15s ease;
  width: 100%;

  &:hover {
    opacity: 0.9;
  }
`;
